"use client";

import { useState, useEffect } from "react";
import { useRouter } from "@/i18n/navigation";
import { useSession } from "@/hooks/use-session";
import { LandingNav } from "@/components/landing/landing-nav";
import { LandingHero } from "@/components/landing/landing-hero";
import { LandingHowItWorks } from "@/components/landing/landing-how-it-works";
import { LandingProductSummary } from "@/components/landing/landing-product-summary";
import { LandingShowcaseCarousel } from "@/components/landing/landing-showcase-carousel";
import { LandingPricing } from "@/components/landing/landing-pricing";
import { LandingFaq } from "@/components/landing/landing-faq";
import { useSiteFlavor } from "@/components/layout/site-flavor-provider";
import { LandingCta } from "@/components/landing/landing-cta";
import { LandingFooter } from "@/components/landing/landing-footer";
import { OnboardingModal } from "@/components/onboarding/onboarding-modal";
import { BrowserBanner } from "@/components/landing/browser-banner";
import { SignupFlowProvider } from "@/components/landing/signup-flow";

const ONBOARDING_SEEN_KEY = "onboarding_seen";

export function LandingPage() {
  const { isAuthenticated } = useSession();
  const { isGetmint, brand } = useSiteFlavor();
  const router = useRouter();

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showBrowserBanner, setShowBrowserBanner] = useState(false);

  const tourMode = brand.onboardingTour;

  // `before-landing` takes over the viewport before the landing renders, so a
  // first-time visitor never sees the page. The other modes always show the
  // page and open the tour later, or not at all.
  useEffect(() => {
    if (tourMode === "before-landing" && !localStorage.getItem(ONBOARDING_SEEN_KEY)) {
      setShowOnboarding(true);
    }
  }, [tourMode]);

  useEffect(() => {
    if (isAuthenticated) {
      router.replace("/new");
    }
  }, [isAuthenticated, router]);

  const handleBannerDismiss = () => {
    setShowBrowserBanner(false);
  };

  const handleOnboardingComplete = () => {
    localStorage.setItem(ONBOARDING_SEEN_KEY, "1");
    router.push("/sign-up");
  };

  // Authenticated visitors always go to the app; the tour is a pitch for people
  // who have not signed up yet.
  const signupFlow =
    tourMode === "on-cta" && !isAuthenticated
      ? { href: null, start: () => setShowOnboarding(true) }
      : { href: isAuthenticated ? "/new" : "/sign-up", start: () => {} };

  if (isAuthenticated) {
    return null;
  }

  if (showOnboarding) {
    // Show browser banner first, then onboarding after dismiss
    if (showBrowserBanner) {
      return <BrowserBanner onDismiss={handleBannerDismiss} />;
    }
    return (
      <OnboardingModal
        isOpen
        onComplete={handleOnboardingComplete}
        source="pre_auth"
      />
    );
  }

  return (
    <SignupFlowProvider value={signupFlow}>
    <div className="min-h-screen bg-landing-bg text-ink">
      <LandingNav />
      <LandingHero />
      <LandingHowItWorks />
      <LandingProductSummary />
      <LandingShowcaseCarousel />
      {/* Pricing is getmintai-only: mint24 sells through the in-app subscription
          modal and its landing has never carried a price table. */}
      {isGetmint && <LandingPricing />}
      <LandingFaq />
      <LandingCta />
      <LandingFooter />
    </div>
    </SignupFlowProvider>
  );
}
